import { AuthProvider } from '@ankr.com/auth';
import { createSelector } from '@reduxjs/toolkit';
import { getWalletIcon, getWalletName } from '@ankr.com/provider';
import { ChainID } from '@ankr.com/chains-list';

import { RootState } from 'store';
import { getChainIcon } from 'uiKit/utils/getTokenIcon';

import { selectIsLightTheme } from '../../layout/store/themeSlice';

const selectAuthState = (state: RootState) => state.auth;

// An address a user logged in with using web3 wallet
export const selectAuthAddress = createSelector(
  selectAuthState,
  state => state.authAddress,
);

export const selectAuthProvider = createSelector(
  selectAuthState,
  state => state.authProvider,
);

export const selectAuthToken = createSelector(
  selectAuthState,
  state => state.authToken,
);

export const selectCallback = createSelector(
  selectAuthState,
  state => state.callback,
);

const selectIsAPIAuthorized = createSelector(
  selectAuthState,
  state => state.isAPIAuthorized,
);

export const selectIsMarketingAccepted = createSelector(
  selectAuthState,
  state => {
    return state.isMarketingAccepted;
  },
);

const selectIsSignedIn = createSelector(
  selectAuthState,
  state => state.isSignedIn,
);

export const selectIsSignedOutManually = createSelector(
  selectAuthState,
  state => state.isSignedOutManually,
);

export const selectIsAPIAuthorizing = createSelector(
  selectIsSignedIn,
  selectIsAPIAuthorized,
  (isSignedIn, isAPIAuthorized) => isSignedIn && !isAPIAuthorized,
);

export const selectReferralCode = createSelector(
  selectAuthState,
  state => state.referralCode,
);

export const selectWalletId = createSelector(
  selectAuthState,
  state => state.walletId,
);

export const selectWalletIcon = createSelector(
  selectWalletId,
  selectIsLightTheme,
  (walletId, isLightTheme) =>
    walletId
      ? getWalletIcon(walletId)
      : getChainIcon(ChainID.ETH, isLightTheme),
);

export const selectWalletName = createSelector(selectWalletId, walletId =>
  walletId ? getWalletName(walletId) : undefined,
);

export const selectIsLoggedIn = createSelector(
  selectIsSignedIn,
  selectIsAPIAuthorized,
  (isSignedIn, isAPIAuthorized) => isSignedIn && isAPIAuthorized,
);

export const selectIsGithubAuth = createSelector(
  selectAuthProvider,
  authProvider => authProvider === AuthProvider.Github,
);

export const selectIsTelegramAuth = createSelector(
  selectAuthProvider,
  authProvider => authProvider === AuthProvider.Telegram,
);

export const selectIsMicrosoftAuth = createSelector(
  selectAuthProvider,
  authProvider => authProvider === AuthProvider.Microsoft,
);

export const selectIsGoogleAuth = createSelector(
  selectAuthProvider,
  authProvider => authProvider === AuthProvider.Google,
);

export const selectIsAuth0 = createSelector(
  selectAuthProvider,
  authProvider => authProvider === AuthProvider.Auth0,
);

export const selectIsSlack = createSelector(
  selectAuthProvider,
  authProvider => authProvider === AuthProvider.Slack,
);

export const selectIsX = createSelector(
  selectAuthProvider,
  authProvider => authProvider === AuthProvider.X,
);

export const selectIsOAuth = createSelector(
  selectIsGithubAuth,
  selectIsGoogleAuth,
  selectIsAuth0,
  selectIsMicrosoftAuth,
  selectIsTelegramAuth,
  selectIsSlack,
  selectIsX,
  (
    isGithubAuth,
    isGoogleAuth,
    isAuth0,
    isMicrosoftAuth,
    isTelegramAuth,
    isSlack,
    isX,
    // eslint-disable-next-line max-params
  ) =>
    isGithubAuth ||
    isGoogleAuth ||
    isAuth0 ||
    isMicrosoftAuth ||
    isTelegramAuth ||
    isSlack ||
    isX,
);

export const selectIsWeb3Auth = createSelector(
  selectAuthProvider,
  authProvider => authProvider === AuthProvider.Web3,
);
